import {
  apiDownload,
  apiDownloadNamed,
  apiFetch,
  apiUpload,
  type OperationResult,
} from "@/lib/apiClient";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";
import { odata } from "@/lib/odata";
import { saveBlob } from "@/utils/download";
import { userRef, toUserRef, type UserRef } from "@/utils/userRef";
import type { InlineImage } from "@/components/RichTextEditor";
import type { BodyFormat } from "@/features/conversations/api/conversations.api";

interface TicketListResponse {
  id: string;
  ticketNumber: number;
  merchantId: string;
  subject: string;
  priority: string;
  status: string;
  assignedToId: string | null;
  createdById: string | null;
  dueAt: string | null;
  creationTime: string;
  mainCategory: string | null;
  category: string | null;
  merchantDbaName: string | null;
  merchantMid: string | null;
  createdByName: string | null;
  assignedToName: string | null;
  commentCount: number | null;
  lastModificationTime: string | null;
  createdBy?: unknown;
  assignedTo?: unknown;
}

interface TicketAttachmentResponse {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number | string;
  createdAt: string;
  url?: string | null;
  urlExpiresAt?: string | null;
  source?: string | null;
  sourceMessageId?: string | null;
  canDelete?: boolean;
  isInline?: boolean;
}

interface TicketAttachmentListResponse {
  totalCount: number | string;
  items: TicketAttachmentResponse[] | null;
}

interface TicketDetailResponse extends TicketListResponse {
  body: string;
  bodyFormat?: string | null;
  hideFromMerchant: boolean;
  notifyMerchant: boolean;
  closedAt: string | null;
  conversationId?: string | null;
}

function toTicketListItem(r: TicketListResponse): TicketListItem {
  return {
    id: r.id,
    ticketNumber: r.ticketNumber,
    subject: r.subject,
    status: r.status,
    priority: r.priority,
    mainCategory: r.mainCategory ?? null,
    category: r.category ?? null,
    merchantId: r.merchantId,
    merchantDbaName: r.merchantDbaName ?? "",
    merchantMid: r.merchantMid ?? "",
    createdBy: r.createdBy ? toUserRef(r.createdBy) : userRef(r.createdById, r.createdByName),
    assignedTo: r.assignedTo ? toUserRef(r.assignedTo) : userRef(r.assignedToId, r.assignedToName),
    commentCount: r.commentCount ?? 0,
    createdAt: r.creationTime,
    updatedAt: r.lastModificationTime ?? r.creationTime,
    dueAt: r.dueAt ?? null,
  };
}

const ATTACHMENT_SOURCES = ["ticket", "description", "comment", "reply"] as const;

function toAttachmentSource(raw: string | null | undefined): TicketAttachmentSource {
  const value = (raw ?? "").toLowerCase();
  return ATTACHMENT_SOURCES.find((s) => s === value) ?? "ticket";
}

function toTicketAttachment(a: TicketAttachmentResponse): TicketAttachment {
  return {
    id: a.id,
    filename: a.fileName,
    contentType: a.contentType ?? "",
    sizeBytes: Number(a.fileSizeBytes) || 0,
    createdAt: a.createdAt,
    url: a.url ?? null,
    urlExpiresAt: a.urlExpiresAt ?? null,
    source: toAttachmentSource(a.source),
    sourceMessageId: a.sourceMessageId ?? null,
    canDelete: a.canDelete !== false,
    isInline: a.isInline === true,
  };
}

function toTicketDetail(r: TicketDetailResponse): TicketDetail {
  return {
    id: r.id,
    ticketNumber: r.ticketNumber,
    subject: r.subject,
    status: r.status,
    priority: r.priority,
    mainCategory: r.mainCategory ?? null,
    category: r.category ?? null,
    body: r.body ?? "",
    bodyFormat: r.bodyFormat === "Markdown" ? "Markdown" : "Plain",
    hideFromMerchant: r.hideFromMerchant ?? false,
    notifyMerchant: r.notifyMerchant ?? false,
    merchantId: r.merchantId,
    merchantDbaName: r.merchantDbaName ?? "",
    merchantMid: r.merchantMid ?? "",
    createdBy: r.createdBy ? toUserRef(r.createdBy) : userRef(r.createdById, r.createdByName),
    assignedTo: r.assignedTo ? toUserRef(r.assignedTo) : userRef(r.assignedToId, r.assignedToName),
    createdAt: r.creationTime,
    updatedAt: r.lastModificationTime ?? r.creationTime,
    closedAt: r.closedAt ?? null,
    conversationId: r.conversationId ?? null,
    dueAt: r.dueAt ?? null,
  };
}

export interface TicketListItem {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string;
  mainCategory: string | null;
  category: string | null;
  merchantId: string;
  merchantDbaName: string;
  merchantMid: string;
  createdBy: UserRef | null;
  assignedTo: UserRef | null;
  commentCount: number;
  createdAt: string;
  updatedAt: string;

  dueAt: string | null;
}

export type TicketAttachmentSource = "ticket" | "description" | "comment" | "reply";

export interface TicketAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  url: string | null;
  urlExpiresAt: string | null;
  source: TicketAttachmentSource;
  sourceMessageId: string | null;
  canDelete: boolean;
  isInline: boolean;
}

export interface TicketAttachmentList {
  totalCount: number;
  items: TicketAttachment[];
}

export interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string;
  mainCategory: string | null;
  category: string | null;
  body: string;
  bodyFormat: BodyFormat;
  hideFromMerchant: boolean;
  notifyMerchant: boolean;
  merchantId: string;
  merchantDbaName: string;
  merchantMid: string;
  createdBy: UserRef | null;
  assignedTo: UserRef | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  conversationId: string | null;

  dueAt: string | null;
}

export interface CreateTicketRequest {
  merchantId: string;
  subject: string;
  mainCategory?: string | null;
  category?: string | null;
  priority?: string | null;
  status?: string | null;
  body?: string | null;
  bodyFormat?: BodyFormat;
  assignedToId?: string | null;
  hideFromMerchant: boolean;
  notifyMerchant: boolean;

  dueAt?: string | null;
}

export interface UpdateTicketRequest {
  subject: string;
  mainCategory?: string | null;
  category?: string | null;
  priority?: string | null;
  status?: string | null;
  body?: string | null;
  bodyFormat?: BodyFormat | null;
  assignedToId?: string | null;
  hideFromMerchant: boolean;
  notifyMerchant: boolean;

  dueAt?: string | null;
}

export interface AssignableUser {
  id: string;
  name: string;
  email: string | null;
}

export interface BulkReassignRequest {
  fromUserId: string;
  toUserId: string;
  includeClosed: boolean;
}

const TICKET_SEARCH_COLS = ["subject"];

const TICKET_STATUS_GROUPS: Record<string, string[]> = {
  Open: ["Open", "InProgress", "Pending"],
  Closed: ["Resolved", "Closed"],
};

export const tickets = {
  async assignableUsers(params: PageParams = {}): Promise<Paged<AssignableUser>> {
    const res = await apiFetch<Paged<AssignableUser> | AssignableUser[]>(
      `/tickets/assignable-users${pageQuery(params, { keyword: params.keyword?.trim() })}`,
    );
    return toPaged<AssignableUser>(res);
  },
  bulkReassign(req: BulkReassignRequest) {
    return apiFetch<OperationResult>("/tickets/bulk-reassign", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },
  async list(
    params: PageParams & { state?: string; merchantId?: string } = {},
  ): Promise<Paged<TicketListItem>> {
    const tab = params.status?.trim();
    const group = tab ? TICKET_STATUS_GROUPS[tab] : undefined;
    const statusClause = group
      ? [odata.or(...group.map((s) => odata.eq("status", s)))]
      : tab
        ? [odata.eq("status", tab)]
        : [];
    const expr = [
      ...(params.expr ?? []),
      ...statusClause,
      ...(params.state?.trim() ? [odata.eq("status", params.state.trim())] : []),
      ...(params.merchantId?.trim() ? [odata.eq("merchantId", params.merchantId.trim())] : []),
    ];
    const res = await apiFetch<Paged<TicketListResponse> | TicketListResponse[]>(
      `/tickets${pageQuery({ ...params, status: undefined, expr }, undefined, TICKET_SEARCH_COLS)}`,
    );
    const p = toPaged<TicketListResponse>(res);
    return { ...p, items: p.items.map(toTicketListItem) };
  },
  listAll(
    params: PageParams & { state?: string; merchantId?: string } = {},
  ): Promise<TicketListItem[]> {
    return fetchAll((p) => this.list(p), params, "tickets");
  },
  async get(id: string) {
    const r = await apiFetch<TicketDetailResponse>(`/tickets/${encodeURIComponent(id)}`);
    return toTicketDetail(r);
  },
  async create(req: CreateTicketRequest) {
    const res = await apiFetch<unknown>("/tickets", {
      method: "POST",
      body: JSON.stringify(req),
    });
    if (typeof res === "string") return this.get(res);
    const r = res as TicketDetailResponse | null;
    return r?.id ? this.get(r.id) : toTicketDetail((r ?? {}) as TicketDetailResponse);
  },
  async update(id: string, req: UpdateTicketRequest) {
    await apiFetch<unknown>(`/tickets/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(req),
    });
    return this.get(id);
  },
  async listAttachments(id: string): Promise<TicketAttachmentList> {
    const res = await apiFetch<TicketAttachmentListResponse | TicketAttachmentResponse[] | null>(
      `/tickets/${encodeURIComponent(id)}/attachments`,
    );
    const items = Array.isArray(res) ? res : (res?.items ?? []);
    return {
      totalCount: Array.isArray(res) ? items.length : Number(res?.totalCount) || items.length,
      items: items.map(toTicketAttachment),
    };
  },
  async uploadInlineImage(ticketId: string | null, file: File): Promise<InlineImage> {
    const form = new FormData();
    form.append("file", file);
    const res = ticketId
      ? await apiUpload<TicketAttachmentResponse>(
          `/tickets/${encodeURIComponent(ticketId)}/attachments`,
          form,
        )
      : await apiUpload<TicketAttachmentResponse>("/attachments", form);
    if (!res?.id) throw new Error("The upload did not return an attachment.");
    const url = res.url ?? (await this.attachmentUrl(res.id));
    if (!url) throw new Error("The upload did not return an image address.");
    return { id: res.id, url };
  },
  async attachmentUrl(attachmentId: string): Promise<string | null> {
    const res = await apiFetch<TicketAttachmentResponse | null>(
      `/attachments/${encodeURIComponent(attachmentId)}`,
    );
    return res?.url ?? null;
  },
  async uploadAttachment(id: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    const res = await apiUpload<TicketAttachmentResponse>(
      `/tickets/${encodeURIComponent(id)}/attachments`,
      form,
    );
    return res?.id ? toTicketAttachment(res) : null;
  },
  deleteAttachment(id: string, attachmentId: string) {
    return apiFetch<void>(
      `/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: "DELETE" },
    );
  },
  attachmentFile(attachmentId: string) {
    return apiDownloadNamed(`/attachments/${encodeURIComponent(attachmentId)}/download`);
  },
  async downloadAttachment(attachmentId: string, filename: string) {
    const blob = await apiDownload(`/attachments/${encodeURIComponent(attachmentId)}/download`);
    saveBlob(blob, filename);
  },
};

export const TICKET_STATUSES = [
  "Open",
  "InProgress",
  "Pending",
  "Resolved",
  "Closed",
] as const;

export const TICKET_PRIORITIES = ["Low", "Normal", "High", "Urgent"] as const;
